import { queryOptions } from '@tanstack/react-query';
import { api, apiBaseUrl, apiSiteSlug } from './api';
import type { ArticleCardData } from '../features/home/components/ArticleCard';
import type { MediaRef } from '../components/media/SmartImage';

const SIDEBAR_ECONOMY_SLUG = 'اقتصاد';

type CategoryItem = {
  id: string;
  slug: string;
  name: string;
  parentId: string | null;
  inMenuOrder: number;
};

export type LayoutData = {
  trendings: { id: string; slug: string; title: string }[];
  newests: ArticleCardData[];
  categories: CategoryItem[];
  mainCategories: CategoryItem[];
  otherCategories: CategoryItem[];
  tvArticles: ArticleCardData[];
};

export type HomeData = {
  last24Hours: ArticleCardData[];
  highlights: ArticleCardData[];
  nationals: ArticleCardData[];
  asfis: ArticleCardData[];
  marrakechs: ArticleCardData[];
  otherRegions: ArticleCardData[];
  tvArticles: ArticleCardData[];
  mainHot: ArticleCardData | null;
  hots: ArticleCardData[];
  politics: ArticleCardData[];
  sports: ArticleCardData[];
  economy: ArticleCardData[];
  sidebar: SidebarData;
};

export type SidebarData = {
  newest: ArticleCardData[];
  trending: ArticleCardData[];
  economy: ArticleCardData[];
};

type Sibling = { slug: string; title: string } | null;

export type ArticleShowData = {
  article: ArticleCardData & {
    title: string;
    html: string;
    excerpt: string | null;
    publishedAt: string | null;
    updatedAt: string | null;
    coverMedia: MediaRef;
    ogImageMedia: MediaRef;
    author: { id: string; name: string } | null;
    metaTitle: string | null;
    metaDescription: string | null;
    canonicalUrl: string | null;
    ogTitle: string | null;
    ogDescription: string | null;
    shortCode: string | null;
    robots: string | null;
    schemaType: string | null;
    readingTimeMinutes: number | null;
    wordCount: number | null;
  };
  previous: Sibling;
  next: Sibling;
  related: ArticleCardData[];
  tags: { id: string; name: string; slug: string }[];
};

/**
 * `/articles/:slug/full` answers 200 with only this field when the requested
 * slug is a retired one the slug-history table still maps: the article exists,
 * at a different URL. Loaders turn it into a 301 instead of rendering it.
 */
export type ArticleSlugRedirect = { redirectTo: string };

export type ArticleShowResult = ArticleShowData | ArticleSlugRedirect;

export function isSlugRedirect(
  data: ArticleShowResult | null | undefined,
): data is ArticleSlugRedirect {
  return !!data && 'redirectTo' in data;
}

export type CategoryShowData = {
  category: {
    id: string;
    name: string;
    slug: string;
    parentChain: { id: string; name: string; slug: string }[];
  };
  articles: ArticleCardData[];
  pagination: { page: number; totalPages: number };
};

type PreviewData = {
  article: {
    title: string;
    contentHtml: string | null;
    coverMedia?: { path: string; url?: string } | null;
    author?: { name: string } | null;
  };
};

export const emptySidebarData: SidebarData = {
  newest: [],
  trending: [],
  economy: [],
};

export const emptyLayoutData: LayoutData = {
  trendings: [],
  newests: [],
  categories: [],
  mainCategories: [],
  otherCategories: [],
  tvArticles: [],
};

export const emptyHomeData: HomeData = {
  last24Hours: [],
  highlights: [],
  nationals: [],
  asfis: [],
  marrakechs: [],
  otherRegions: [],
  tvArticles: [],
  mainHot: null,
  hots: [],
  politics: [],
  sports: [],
  economy: [],
  sidebar: emptySidebarData,
};

/**
 * Carries the upstream HTTP status so route loaders can tell "this article does
 * not exist" apart from "the API is down". Without the status every failure
 * looked identical and a missing slug rendered a 500 — a status Google retries
 * and never drops from the index, instead of the 404 that removes it.
 */
export class ApiHttpError extends Error {
  constructor(
    public status: number,
    public body?: unknown,
  ) {
    super(`api error ${status}: ${JSON.stringify(body ?? '')}`);
    this.name = 'ApiHttpError';
  }
}

export function isNotFoundError(err: unknown): boolean {
  return err instanceof ApiHttpError && (err.status === 404 || err.status === 410);
}

function unwrap<T>(res: { data: unknown; error: unknown }): T {
  if (res.error) {
    const err = res.error as { status?: number; value?: unknown };
    throw new ApiHttpError(err?.status ?? 0, err?.value);
  }
  return res.data as T;
}

export const layoutQuery = () =>
  queryOptions({
    queryKey: ['layout'],
    queryFn: async () => unwrap<LayoutData>(await api.layout.get()),
    staleTime: 60_000,
  });

export const homeQuery = () =>
  queryOptions({
    queryKey: ['home'],
    queryFn: async () =>
      unwrap<HomeData>(await api.home.get({ query: { economy: SIDEBAR_ECONOMY_SLUG } })),
    staleTime: 30_000,
  });

export const sidebarQuery = () =>
  queryOptions({
    queryKey: ['sidebar'],
    queryFn: async () =>
      unwrap<SidebarData>(await api.sidebar.get({ query: { economy: SIDEBAR_ECONOMY_SLUG } })),
    staleTime: 60_000,
  });

export const articleShowQuery = (slug: string) =>
  queryOptions({
    queryKey: ['article', slug],
    queryFn: async () =>
      unwrap<ArticleShowResult>(await api.articles({ articleSlug: slug }).full.get()),
    staleTime: 30_000,
  });

export const categoryShowQuery = (slug: string, page: number) =>
  queryOptions({
    queryKey: ['category', slug, page],
    queryFn: async () => {
      // Slugs may contain `/` (legacy nested categories like `الجهة/قلعة-السراغنة`).
      // Eden treaty's typed param accessor doesn't model multi-segment paths,
      // so we fetch the wildcard route directly. Encode segments individually
      // to preserve the slash separator on the wire.
      const path = slug
        .split('/')
        .filter(Boolean)
        .map(encodeURIComponent)
        .join('/');
      const res = await fetch(`${apiBaseUrl}/category/${path}?page=${page}`, {
        credentials: 'include',
        headers: { 'X-Site-Slug': apiSiteSlug },
      });
      if (!res.ok) {
        throw new ApiHttpError(res.status, await res.text());
      }
      return (await res.json()) as CategoryShowData;
    },
    staleTime: 30_000,
  });

export const previewQuery = (token: string) =>
  queryOptions({
    queryKey: ['preview', token],
    queryFn: async () => unwrap<PreviewData>(await api.preview({ token }).get()),
    staleTime: 0,
  });
